import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  buildConsentContentHtml,
  CONSENT_PRINT_PAGE_STYLES,
  readConsentPreviewPayload,
} from '../lib/consentDocument';

/**
 * Vista previa del consentimiento en pestaña nueva (imprimible → PDF en el navegador).
 * Los botones usan React (no onclick inline) para respetar CSP script-src-attr 'none'.
 */
export default function AdminConsentPreview() {
  const payload = useMemo(() => readConsentPreviewPayload(), []);
  const [searchParams] = useSearchParams();
  const autoPrint = searchParams.get('print') === '1';

  const contentHtml = useMemo(() => {
    if (!payload) return '';
    return buildConsentContentHtml({
      patientName: payload.patientName,
      podiatristName: payload.podiatristName,
      signedAt: payload.signedAt,
      clinicPlace: payload.clinicPlace,
      folio: payload.folio,
    });
  }, [payload]);

  useEffect(() => {
    document.body.classList.add('consent-preview-open');
    return () => document.body.classList.remove('consent-preview-open');
  }, []);

  useEffect(() => {
    if (payload) {
      document.title = `Consentimiento — ${payload.patientName || 'Paciente'}`;
    }
  }, [payload]);

  useEffect(() => {
    if (!autoPrint || !payload) return undefined;
    const timer = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(timer);
  }, [autoPrint, payload]);

  if (!payload) {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4 p-8 text-center">
        <p className="text-muted-foreground">
          No hay datos de consentimiento para mostrar. Finalice la historia clínica desde el expediente o
          abra de nuevo el paso de consentimiento.
        </p>
        <Link to="/admin/expedientes" className="text-primary underline">
          Volver a expedientes
        </Link>
      </div>
    );
  }

  return (
    <>
      <style>{CONSENT_PRINT_PAGE_STYLES}</style>
      <div className="consent-print-page has-toolbar">
        <div className="consent-print-toolbar">
          <button type="button" onClick={() => window.print()}>
            Imprimir / Guardar PDF
          </button>
          <button type="button" onClick={() => window.close()}>
            Cerrar
          </button>
          <span className="toolbar-hint">
            En el diálogo de impresión elija «Guardar como PDF» o imprima en papel.
          </span>
        </div>
        <p className="clinic">{payload.clinicName || 'PodoclinicAM'}</p>
        <div className="doc-content" dangerouslySetInnerHTML={{ __html: contentHtml }} />
      </div>
    </>
  );
}
